import ResponsiveTable from '../../components/ResponsiveTable';
import React, { useState, useEffect } from 'react';
import CustomDateFields from '../../components/CustomDateFields';
import FilterScroller from '../../components/FilterScroller';
import { useNavigate } from 'react-router-dom';
import { useOnChange, useUrlFilters, useUrlState } from '../../hooks/useUrlState';
import Swal from 'sweetalert2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faBoxOpen, faPlus, faSearch, faEdit, faFileInvoice,
    faImage, faFilter, faCheckCircle, faClock, faTimesCircle, 
    faUndo, faWallet, faTrash, faFileContract, faSort, faSortUp, faSortDown
} from '@fortawesome/free-solid-svg-icons';
import Pagination from '../../components/Pagination'; 
import { useRecentSearches } from '../../components/RecentSearches';
import '../../styles/App.css';
import '../../styles/expenses.css';
import api, { SERVER_URL } from '../../utils/api';

const Expenses = () => {
    const navigate = useNavigate();
    const currentUser = JSON.parse(localStorage.getItem('user')); 
    
    const [expenses, setExpenses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [walletBalance, setWalletBalance] = useState(0);

    // --- PAGINATION & STATS STATES ---
    const [currentPage, setCurrentPage] = useUrlState('page', 1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    const [sortConfig, setSortConfig] = useUrlFilters({ key: 'expenseDate', direction: 'desc' }, 'sort-');
    
    const [stats, setStats] = useState({
        pendingTotal: 0, pendingCount: 0,
        approvedTotal: 0, approvedCount: 0,
        returnedTotal: 0, returnedCount: 0,
        rejectedTotal: 0, rejectedCount: 0,
        gstTotal: 0, gstCount: 0,
        totalFilteredAmount: 0
    });

    // --- FILTER STATES ---
    const [filterType, setFilterType] = useUrlState('range', 'All');
    // The address holds the search once typing pauses; the box starts from it.
    const [debouncedSearch, setDebouncedSearch] = useUrlState('q', '');
    const [searchTerm, setSearchTerm] = useState(debouncedSearch);
    const recentSearch = useRecentSearches('my-expenses', { value: searchTerm, onPick: setSearchTerm });
    const [customDates, setCustomDates] = useUrlFilters({ from: '', to: '' });
    const [statusFilter, setStatusFilter] = useUrlState('status', 'All');
    const [hasGstFilter, setHasGstFilter] = useUrlState('gst', 'All');

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(searchTerm), 500);
        return () => clearTimeout(timer);
    }, [searchTerm, setDebouncedSearch]);

    useOnChange(() => setCurrentPage(1), [filterType, customDates, statusFilter, hasGstFilter, debouncedSearch, sortConfig]);

    useEffect(() => {
        fetchExpenses(currentPage);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, itemsPerPage, filterType, customDates, statusFilter, hasGstFilter, debouncedSearch, sortConfig]);

    const fetchExpenses = async (pageToFetch) => {
        setLoading(true);
        try {
            const params = {
                page: pageToFetch,
                limit: itemsPerPage,
                search: debouncedSearch,
                status: statusFilter,
                hasGst: hasGstFilter === 'All' ? '' : hasGstFilter,
                filterType: filterType,
                fromDate: customDates.from,
                toDate: customDates.to,
                sortBy: sortConfig.key,
                sortOrder: sortConfig.direction
            };

            const res = await api.get('/expenses', { params });
            
            setExpenses(res.data.data || []);
            if (res.data.stats) setStats(res.data.stats);
            setTotalPages(res.data.pagination?.totalPages || 1);
            setTotalRecords(res.data.pagination?.totalRecords || 0);
            setCurrentPage(res.data.pagination?.currentPage || 1);

            try {
                const walletRes = await api.get('/wallets/my-balance');
                setWalletBalance(walletRes.data.balance || 0);
            } catch (err) {
                console.error("Wallet API not found yet", err);
            }

        } catch (err) {
            console.error("Error fetching expenses", err);
            Swal.fire('Error', 'Failed to load expenses', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteExpense = async (id) => {
        const confirm = await Swal.fire({
            title: 'Delete this Expense?',
            text: "This action cannot be undone.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc2626',
            cancelButtonColor: '#64748b',
            confirmButtonText: 'Yes, delete it!'
        });

        if (confirm.isConfirmed) {
            try {
                await api.delete(`/expenses/${id}`);
                Swal.fire('Deleted!', 'Your expense has been deleted.', 'success');
                fetchExpenses(currentPage);
            } catch (err) {
                Swal.fire('Error', 'Failed to delete expense. It may be locked.', 'error');
            }
        }
    };

    const handleSort = (key) => {
        let direction = 'desc';
        if (sortConfig.key === key && sortConfig.direction === 'desc') {
            direction = 'asc';
        }
        setSortConfig({ key, direction });
    };

    const getSortIcon = (columnName) => {
        if (sortConfig.key !== columnName) return <FontAwesomeIcon icon={faSort} style={{ color: '#cbd5e1', marginLeft: '5px' }} />;
        if (sortConfig.direction === 'asc') return <FontAwesomeIcon icon={faSortUp} style={{ color: 'light-dark(#2563eb, var(--theme-blue-ink, #91b7ff))', marginLeft: '5px' }} />;
        return <FontAwesomeIcon icon={faSortDown} style={{ color: 'light-dark(#2563eb, var(--theme-blue-ink, #91b7ff))', marginLeft: '5px' }} />;
    };

    const handleCardClick = (type, value) => {
        if (type === 'status') {
            setStatusFilter(prev => prev === value ? 'All' : value);
        } else if (type === 'hasGst') {
            setHasGstFilter(prev => prev === value ? 'All' : value);
        }
    };

    const getFileUrl = (url) => {
        if (!url) return '';
        return url.startsWith('http') ? url : `${SERVER_URL}${url}`;
    };

    const viewFile = (fileData, title) => {
        recentSearch.save();
        if (Array.isArray(fileData)) {
            let htmlContent = '<div style="display:flex; flex-direction:column; gap:20px; max-height: 60vh; overflow-y:auto; padding-right:10px;">';
            fileData.forEach((url, index) => {
                const fullUrl = getFileUrl(url);
                const isVideo = url.toLowerCase().match(/\.(mp4|webm|ogg|mov)$/);
                const isPdf = url.toLowerCase().endsWith('.pdf');

                let mediaElement = '';
                if (isVideo) {
                    mediaElement = `<video src="${fullUrl}" controls style="width:100%; border-radius:6px; max-height:400px; background:#000;"></video>`;
                } else if (isPdf) {
                    mediaElement = `<iframe src="${fullUrl}" width="100%" height="400px" style="border: none; border-radius: 6px;"></iframe>`;
                } else {
                    mediaElement = `<img src="${fullUrl}" style="width:100%; border-radius:6px; max-height:400px; object-fit:contain;" />`;
                }

                htmlContent += `
                    <div style="background: light-dark(#f8fafc, var(--theme-surface-soft, #202327)); padding: 10px; border-radius: 8px; border: 1px solid light-dark(#e2e8f0, var(--theme-border, #33363b));">
                        <div style="text-align: left; font-size: 12px; color: light-dark(#64748b, var(--theme-muted, #959ba5)); margin-bottom: 8px; font-weight: 600;">File ${index + 1}</div>
                        ${mediaElement}
                    </div>`;
            });
            htmlContent += '</div>';
            Swal.fire({ title: title, html: htmlContent, width: '800px', showCloseButton: true, showConfirmButton: false });
        } else {
            const fullUrl = getFileUrl(fileData);
            const isPdf = fileData.toLowerCase().endsWith('.pdf');
            if (isPdf) { Swal.fire({ title: title, html: `<iframe src="${fullUrl}" width="100%" height="500px" style="border: none; border-radius: 8px;"></iframe>`, width: '800px', showCloseButton: true, showConfirmButton: false }); }
            else { Swal.fire({ title: title, imageUrl: fullUrl, imageAlt: title, width: '800px', showCloseButton: true, showConfirmButton: false }); }
        }
    };

    const getStatusIcon = (status) => {
        if (status === 'Approved') return <FontAwesomeIcon icon={faCheckCircle} style={{ color: 'light-dark(#16a34a, var(--theme-success-ink))', marginRight: '5px' }} />;
        if (status === 'Rejected') return <FontAwesomeIcon icon={faTimesCircle} style={{ color: 'light-dark(#dc2626, var(--theme-danger-ink))', marginRight: '5px' }} />;
        if (status === 'Returned') return <FontAwesomeIcon icon={faUndo} style={{ color: 'light-dark(#ea580c, var(--theme-warning-ink, #f0c27a))', marginRight: '5px' }} />;
        return <FontAwesomeIcon icon={faClock} style={{ color: 'light-dark(#d97706, var(--theme-warning-ink))', marginRight: '5px' }} />;
    };

    const getSpecificDetail = (item) => {
        if (item.category === 'Vendor Payment' && item.vendorId?.name) return item.vendorId.name;
        if (item.category === 'Participant Payment') return item.expenseDetails?.participantName || item.expenseDetails?.name || item.descriptionTags || 'Participant Payment';
        if (item.category === 'Product / Item Purchase') {
            if (item.expenseDetails?.items && item.expenseDetails.items.length > 0) {
                const firstItem = item.expenseDetails.items[0].productName;
                const extraCount = item.expenseDetails.items.length - 1;
                return extraCount > 0 ? `${firstItem} (+${extraCount} more)` : firstItem;
            }
            return item.expenseDetails?.itemName || item.expenseDetails?.productName || item.descriptionTags || 'Product Purchase';
        }
        return item.descriptionTags || 'No specific details provided';
    };

    const getMinimalCardStyle = (type, value, colorHex) => {
        const isActive = type === 'status' ? statusFilter === value : hasGstFilter === value;
        const isAll = type === 'status' ? statusFilter === 'All' : hasGstFilter === 'All';
        
        return {
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            opacity: isAll || isActive ? 1 : 0.4,
            border: isActive ? `1.5px solid ${colorHex}` : '1px solid #e2e8f0',
            background: 'light-dark(#ffffff, var(--theme-surface))',
            padding: '12px 16px',
            borderRadius: '8px',
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
            minWidth: '140px',
            boxShadow: isActive ? `0 2px 8px ${colorHex}15` : 'none'
        };
    };

    return (
        <div className="settings-container my-expenses-page fade-in">
            <div className="page-header-row mb-20">
                <h1 className="page-title header-no-margin">
                    <FontAwesomeIcon icon={faBoxOpen} className="btn-icon" /> My Expenses
                </h1>

                <button className="action-btn-primary btn-small m-0" onClick={() => navigate('/add-expense')}>
                    <FontAwesomeIcon icon={faPlus} className="btn-icon" /> Log Expense
                </button>
            </div>

            <div className="expense-summary-grid" style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '25px', alignItems: 'stretch' }}>

                {/* Wallet Pill - Bulletproof formatting */}
                <div style={{ background: walletBalance < 0 ? 'light-dark(#fef2f2, var(--theme-danger-soft, #432a31))' : 'light-dark(#f0fdf4, var(--theme-success-soft, #1a3a31))', border: `1px solid ${walletBalance < 0 ? 'light-dark(#fecaca, var(--theme-border-strong, #4a4e55))' : 'light-dark(#bbf7d0, var(--theme-border-strong, #4a4e55))'}`, padding: '12px 16px', borderRadius: '8px', display: 'flex', flexDirection: 'column', minWidth: '160px', gap: '4px' }}>
                    <div style={{ fontSize: '12px', color: walletBalance < 0 ? 'light-dark(#dc2626, var(--theme-danger-ink, #f09a9d))' : 'light-dark(#16a34a, var(--theme-success-ink, #7bcf9f))', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <FontAwesomeIcon icon={faWallet} /> Wallet Balance
                    </div>
                    <div style={{ fontSize: '18px', fontWeight: 'bold', color: walletBalance < 0 ? 'light-dark(#b91c1c, var(--theme-danger-ink, #f09a9d))' : 'light-dark(#15803d, var(--theme-success-ink, #7bcf9f))' }}>
                        {walletBalance < 0 ? '-' : ''}₹ {(Math.abs(walletBalance) || 0).toLocaleString('en-IN')}
                    </div>
                </div>

                <div className="expense-summary-divider" style={{ width: '1px', background: 'light-dark(#e2e8f0, var(--theme-surface-muted))', margin: '0 5px' }}></div>

                {/* Metric Pills - Bulletproof formatting */}
                <div style={getMinimalCardStyle('status', 'Pending', '#d97706')} onClick={() => handleCardClick('status', 'Pending')}>
                    <div style={{ fontSize: '12px', color: 'light-dark(#64748b, var(--theme-muted))', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <FontAwesomeIcon icon={faClock} style={{ color: 'light-dark(#d97706, var(--theme-warning-ink))' }} /> Pending
                    </div>
                    <div style={{ fontSize: '16px', fontWeight: 'bold', color: 'light-dark(#0f172a, var(--theme-ink))' }}>₹ {(stats?.pendingTotal || 0).toLocaleString('en-IN')}</div>
                    <div style={{ fontSize: '11px', color: 'light-dark(#94a3b8, var(--theme-subtle))' }}>{stats?.pendingCount || 0} items</div>
                </div>

                <div style={getMinimalCardStyle('status', 'Approved', '#16a34a')} onClick={() => handleCardClick('status', 'Approved')}>
                    <div style={{ fontSize: '12px', color: 'light-dark(#64748b, var(--theme-muted))', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <FontAwesomeIcon icon={faCheckCircle} style={{ color: 'light-dark(#16a34a, var(--theme-success-ink))' }} /> Accepted
                    </div>
                    <div style={{ fontSize: '16px', fontWeight: 'bold', color: 'light-dark(#0f172a, var(--theme-ink))' }}>₹ {(stats?.approvedTotal || 0).toLocaleString('en-IN')}</div>
                    <div style={{ fontSize: '11px', color: 'light-dark(#94a3b8, var(--theme-subtle))' }}>{stats?.approvedCount || 0} items</div>
                </div>

                <div style={getMinimalCardStyle('status', 'Returned', '#ea580c')} onClick={() => handleCardClick('status', 'Returned')}>
                    <div style={{ fontSize: '12px', color: 'light-dark(#64748b, var(--theme-muted))', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <FontAwesomeIcon icon={faUndo} style={{ color: 'light-dark(#ea580c, var(--theme-warning-ink, #f0c27a))' }} /> Returned
                    </div>
                    <div style={{ fontSize: '16px', fontWeight: 'bold', color: 'light-dark(#0f172a, var(--theme-ink))' }}>₹ {(stats?.returnedTotal || 0).toLocaleString('en-IN')}</div>
                    <div style={{ fontSize: '11px', color: 'light-dark(#94a3b8, var(--theme-subtle))' }}>{stats?.returnedCount || 0} items</div>
                </div>

                <div className="expense-summary-divider" style={{ width: '1px', background: 'light-dark(#e2e8f0, var(--theme-surface-muted))', margin: '0 5px' }}></div>

                {/* GST Card Filter */}
                <div style={getMinimalCardStyle('hasGst', 'Yes', '#8b5cf6')} onClick={() => handleCardClick('hasGst', 'Yes')}>
                    <div style={{ fontSize: '12px', color: 'light-dark(#64748b, var(--theme-muted))', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px' }}><FontAwesomeIcon icon={faFileContract} style={{ color: '#8b5cf6' }} /> With Valid GST</div>
                    <div style={{ fontSize: '16px', fontWeight: 'bold', color: 'light-dark(#0f172a, var(--theme-ink))' }}>₹ {(stats?.gstTotal || 0).toLocaleString('en-IN')}</div>
                    <div style={{ fontSize: '11px', color: 'light-dark(#94a3b8, var(--theme-subtle))' }}>{stats?.gstCount || 0} items</div>
                </div>
            </div>

            <div className="filter-bar-card fade-in">
                <FilterScroller>
                    {['Today', 'Week', 'Month', 'All', 'Custom'].map(type => (
                        <button key={type} className={`gts-btn filter-btn ${filterType === type ? 'primary active' : 'warning inactive'}`} onClick={() => setFilterType(type)}>
                            {type === 'Custom' && <FontAwesomeIcon icon={faFilter} className="filter-icon" />} {type}
                        </button>
                    ))}
                </FilterScroller>

                {filterType === 'Custom' && <CustomDateFields value={customDates} onChange={setCustomDates} />}

                <div className="search-wrapper">
                    <FontAwesomeIcon icon={faSearch} className="search-icon" />
                    <input {...recentSearch.inputProps} type="text" placeholder="Search category, tags, amount..." className="swal2-input search-input" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                    {recentSearch.menu}
                </div>
            </div>

            <div className="table-summary-text fade-in" style={{ marginBottom: '20px', fontSize: '14px', fontWeight: '600', color: 'light-dark(#64748b, var(--theme-muted))', textAlign: 'right' }}>
                <span>Showing {expenses.length} of {totalRecords} records</span><span className="expense-summary-separator"> &nbsp;|&nbsp; </span><span>View Total: <span style={{ color: 'light-dark(#0f172a, var(--theme-ink))' }}>₹ {(stats?.totalFilteredAmount || 0).toLocaleString('en-IN')}</span></span>
            </div>

            <div className="employee-table-container fade-in">
                <ResponsiveTable className="employee-table expense-records">
                    <thead>
                        <tr>
                            <th>Expense Category</th>
                            <th>Project & Details</th> 
                            <th 
                                onClick={() => handleSort('amount')} 
                                style={{ cursor: 'pointer', userSelect: 'none' }}
                                title="Click to sort by Amount"
                            >
                                Amount {getSortIcon('amount')}
                            </th>
                            <th 
                                onClick={() => handleSort('expenseDate')} 
                                style={{ cursor: 'pointer', userSelect: 'none' }}
                                title="Click to sort by Date"
                            >
                                Date {getSortIcon('expenseDate')}
                            </th>
                            <th>Payment Source</th>
                            <th>Status</th>
                            <th>Proof</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan="8" className="empty-table-message">Loading Server Data...</td></tr>
                        ) : expenses.length === 0 ? (
                            <tr><td colSpan="8" className="empty-table-message">No records found.</td></tr>
                        ) : (
                            expenses.map(item => {
                                const isProject = item.expenseType === 'Project Expense';
                                const typeLabel = isProject ? 'Project' : 'Regular';
                                const typeBorderColor = isProject ? '#A6477F' : '#1E73BE';
                                const typeBgColor = isProject ? '#fdf2f8' : '#eff6ff';

                                return (
                                    <tr key={item._id}>
                                        <td data-label="Category">
                                            <div className="fw-600 text-primary" style={{ marginBottom: '6px' }}>{item.category}</div>
                                            <span 
                                                style={{ 
                                                    background: typeBgColor, 
                                                    color: typeBorderColor, 
                                                    border: `1px solid ${typeBorderColor}`, 
                                                    padding: '3px 8px', 
                                                    borderRadius: '4px', 
                                                    fontSize: '10px', 
                                                    fontWeight: '600', 
                                                    display: 'inline-block' 
                                                }}
                                            >
                                                {typeLabel}
                                            </span>
                                        </td>

                                        <td data-label="Project & Details">
                                            <div className="fw-bold text-dark" style={{ fontSize: '13px', marginBottom: '2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '220px' }} title={getSpecificDetail(item)}>
                                                {getSpecificDetail(item)}
                                            </div>
                                            <div className="fw-500 text-small text-muted" style={{ marginBottom: '4px' }}>
                                                {item.projectName || 'Regular Office'}
                                            </div>
                                            <div className={`expense-tag-pill ${item.descriptionTags === getSpecificDetail(item) ? 'expense-detail-repeat' : ''}`}>{item.descriptionTags}</div>
                                        </td>

                                        <td data-label="Amount">
                                            <div className="expense-amount-large">₹ {(item.amount || 0).toLocaleString('en-IN')}</div>
                                            {item.expenseDetails?.gstNumber && <span style={{ fontSize: '10px', background: 'light-dark(#dcfce7, var(--theme-success-soft))', color: 'light-dark(#16a34a, var(--theme-success-ink))', padding: '2px 6px', borderRadius: '4px', marginTop: '4px', display: 'inline-block' }}>GST: {item.expenseDetails.gstNumber}</span>}
                                        </td>

                                        <td data-label="Date">
                                            <div className="text-small text-dark fw-600">{item.expenseDate ? new Date(item.expenseDate).toLocaleDateString('en-GB') : '-'}</div>
                                            {item.category === 'Vendor Payment' && <div className="text-muted text-small mt-5">Inv Date</div>}
                                        </td>

                                        <td data-label="Payment Source">
                                            {item.submittedBy?._id && item.submittedBy._id !== currentUser.id && (
                                                <div className="text-small text-muted" style={{ marginBottom: '4px' }}>
                                                    <span className="fw-600">Logged by:</span> {item.submittedBy.name}
                                                </div>
                                            )}
                                            <div className="text-small">
                                                <span className="fw-600 text-dark">Paid by:</span> {
                                                    item.isCompanyPayment ? 'Company Account' : 
                                                    item.paymentSourceId?._id === currentUser.id ? 'Me' : 
                                                    item.paymentSourceId?.name || 'Me'
                                                }
                                            </div>
                                        </td>

                                        <td data-label="Status">
                                            <span className={`status-badge ${item.status === 'Approved' ? 'success' : item.status === 'Rejected' ? 'danger' : item.status === 'Returned' ? 'warning' : 'warning'}`} style={{ padding: '6px 10px', fontSize: '11px', display: 'inline-flex', alignItems: 'center' }}>
                                                {getStatusIcon(item.status)} {item.status || 'Pending'}
                                            </span>
                                            {item.approvedBy && (
                                                <div style={{ fontSize: '11px', color: 'light-dark(#64748b, var(--theme-muted))', marginTop: '4px', fontStyle: 'italic' }}>
                                                    By: {item.approvedBy.name}
                                                </div>
                                            )}
                                            {item.status === 'Returned' && item.adminFeedback && (
                                                <div style={{ fontSize: '11px', color: 'light-dark(#ea580c, var(--theme-warning-ink, #f0c27a))', marginTop: '4px', fontWeight: 'bold' }}>
                                                    Note: "{item.adminFeedback}"
                                                </div>
                                            )}
                                        </td>

                                        <td data-label="Proof">
                                            <div className="flex-row gap-5 flex-wrap">
                                                {item.paymentScreenshotUrls && item.paymentScreenshotUrls.length > 0 ? (
                                                    <button onClick={() => viewFile(item.paymentScreenshotUrls, `Payment Proofs (${item.paymentScreenshotUrls.length})`)} className="gts-btn doc-btn doc-proof" title="View Proofs">
                                                        <FontAwesomeIcon icon={faFileInvoice} />
                                                    </button>
                                                ) : item.paymentScreenshotUrl ? (
                                                    <button onClick={() => viewFile(item.paymentScreenshotUrl, 'Payment Proof')} className="gts-btn doc-btn doc-proof" title="View Proof">
                                                        <FontAwesomeIcon icon={faFileInvoice} />
                                                    </button>
                                                ) : <span className="text-muted">-</span>}

                                                {item.expenseMediaUrls && item.expenseMediaUrls.length > 0 ? (
                                                    <button onClick={() => viewFile(item.expenseMediaUrls, `Media (${item.expenseMediaUrls.length})`)} className="gts-btn doc-btn doc-media" title="View Media">
                                                        <FontAwesomeIcon icon={faImage} />
                                                    </button>
                                                ) : null}
                                            </div>
                                        </td>

                                        <td data-label="Actions">
                                            {item.status === 'Pending' || item.status === 'Returned' ? (
                                                <div style={{ display: 'flex', gap: '5px' }}>
                                                    <button className="gts-btn primary btn-small m-0" onClick={() => { recentSearch.save(); navigate(`/edit-expense/${item._id}`); }}>
                                                        <FontAwesomeIcon icon={faEdit} className="btn-icon" /> {item.status === 'Returned' ? 'Fix' : 'Edit'}
                                                    </button>
                                                    <button className="gts-btn danger btn-small m-0" onClick={() => handleDeleteExpense(item._id)} title="Delete Expense">
                                                        <FontAwesomeIcon icon={faTrash} />
                                                    </button>
                                                </div>
                                            ) : (
                                                <span className="text-small text-muted">Locked</span>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </ResponsiveTable>
            </div>

            {!loading && (
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalRecords={totalRecords}
                    limit={itemsPerPage}
                    onPageChange={(page) => setCurrentPage(page)}
                    onLimitChange={(newLimit) => {
                        setItemsPerPage(newLimit);
                        setCurrentPage(1);
                    }}
                />
            )}
        </div>
    );
};

export default Expenses;
